/**
 * SettingsPage — perfil del usuario actual + cambio de contraseña + sesión.
 *
 * Conecta con los endpoints reales:
 *   PATCH /api/auth/me           → actualizar nombre
 *   POST  /api/auth/change-password
 *   POST  /api/auth/login (re-login si fuerza cierre tras cambio de password)
 *
 * El logout limpia el store de auth y vuelve a /login.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User as UserIcon, Lock, LogOut, Save, ShieldCheck, KeyRound, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore, type AuthUser, isAdmin } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (!user) {
    return (
      <div className="px-8 py-8">
        <h1 className="text-2xl">Configuración</h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          No hay sesión activa.
        </p>
      </div>
    );
  }

  return (
    <div className="px-8 py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl">Configuración</h1>
          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
            Tu perfil, seguridad de la cuenta y sesión actual.
          </p>
        </div>
        <Button variant="ghost" size="md" onClick={onLogout}>
          <LogOut size={16} strokeWidth={1.8} />
          Cerrar sesión
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Profile summary */}
        <div className="lg:col-span-1">
          <SectionCard title="Cuenta" icon={UserIcon}>
            <div className="flex items-center gap-3">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full text-base font-semibold"
                style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
              >
                {(user.name || user.email)[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {user.name || '(sin nombre)'}
                </div>
                <div className="truncate text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                  {user.email}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-[11.5px]">
              <Row label="Rol" value={
                <span className="inline-flex items-center gap-1.5">
                  {isAdmin(user) && <ShieldCheck size={12} style={{ color: 'var(--accent)' }} />}
                  <span style={{ color: 'var(--text-primary)' }}>{user.role.label}</span>
                </span>
              } />
              <Row label="Estado" value={
                <span style={{ color: user.is_active ? 'var(--success)' : 'var(--danger)' }}>
                  {user.is_active ? '● Activo' : '○ Inactivo'}
                </span>
              } />
              <Row label="ID" value={<code className="font-mono">{user.id}</code>} />
              <Row label="Cuenta creada" value={new Date(user.created_at).toLocaleString('es-PE', { dateStyle: 'long', timeStyle: 'short' })} />
              <Row label="Actualizada" value={new Date(user.updated_at).toLocaleString('es-PE', { dateStyle: 'long', timeStyle: 'short' })} />
            </div>

            {user.role.permissions.length > 0 && (
              <div className="mt-4">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--text-muted)' }}>
                  Permisos
                </div>
                <div className="flex flex-wrap gap-1">
                  {user.role.permissions.map((p) => (
                    <span key={p} className="rounded-full px-2 py-0.5 text-[10px] font-mono"
                      style={{ background: 'var(--surface-2)', color: 'var(--text-secondary)' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        </div>

        {/* Profile + password editor */}
        <div className="lg:col-span-2 space-y-4">
          <ProfileForm user={user} onSaved={setUser} />
          <PasswordForm />
          <PreferencesCard />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

function SectionCard({
  title, icon: Icon, children,
}: {
  title: string;
  icon: typeof UserIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border p-4"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-3 flex items-center gap-2">
        <Icon size={14} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="text-right" style={{ color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

function ProfileForm({ user, onSaved }: { user: AuthUser; onSaved: (u: AuthUser) => void }) {
  const [name, setName] = useState(user.name);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.patch<AuthUser>('/api/auth/me', { name });
      onSaved(data);
      setMessage({ type: 'success', text: 'Perfil actualizado correctamente.' });
    } catch (err) {
      let msg = 'No se pudo guardar.';
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'string' ? detail : msg;
      }
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Perfil" icon={UserIcon}>
      <form onSubmit={onSubmit} className="space-y-3">
        <FormField label="Nombre completo">
          <input
            type="text"
            value={name}
            minLength={2}
            maxLength={120}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
          />
        </FormField>
        <FormField label="Correo electrónico">
          <input
            type="email"
            value={user.email}
            disabled
            className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm opacity-60"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
          />
          <div className="mt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
            El correo no se puede cambiar desde aquí. Contacta al administrador si necesitas modificarlo.
          </div>
        </FormField>

        {message && (
          <div className="rounded-[var(--radius-sm)] border px-3 py-2 text-[11.5px]"
            style={{
              background: message.type === 'success' ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
              borderColor: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
              color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
            }}>
            {message.text}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" variant="primary" size="md" disabled={saving || name === user.name}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
            {!saving && <Save size={14} strokeWidth={2} />}
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}

/* ---------------------------------------------------------------------- */

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      if (next !== confirm) {
        throw new Error('Las contraseñas nuevas no coinciden.');
      }
      if (next === current) {
        throw new Error('La nueva contraseña debe ser distinta de la actual.');
      }
      await api.post('/api/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setMessage({ type: 'success', text: 'Contraseña actualizada. Considera cerrar y volver a iniciar sesión.' });
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      let msg = 'No se pudo cambiar la contraseña.';
      if (err instanceof Error && !axios.isAxiosError(err)) {
        msg = err.message;
      } else if (axios.isAxiosError(err) && err.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'string' ? detail : msg;
      }
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Seguridad — Cambiar contraseña" icon={KeyRound}>
      <form onSubmit={onSubmit} className="space-y-3">
        <FormField label="Contraseña actual">
          <PasswordInput value={current} onChange={setCurrent} />
        </FormField>
        <FormField label="Nueva contraseña (mín. 8 caracteres)">
          <PasswordInput value={next} onChange={setNext} minLength={8} />
        </FormField>
        <FormField label="Repetir nueva contraseña">
          <PasswordInput value={confirm} onChange={setConfirm} minLength={8} />
        </FormField>

        {message && (
          <div className="rounded-[var(--radius-sm)] border px-3 py-2 text-[11.5px]"
            style={{
              background: message.type === 'success' ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
              borderColor: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
              color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
            }}>
            {message.text}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" variant="primary" size="md"
            disabled={saving || !current || !next || !confirm}>
            {saving ? 'Actualizando…' : 'Cambiar contraseña'}
            {!saving && <Lock size={14} strokeWidth={2} />}
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}

function PasswordInput({
  value, onChange, minLength,
}: { value: string; onChange: (v: string) => void; minLength?: number }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        minLength={minLength}
        onChange={(e) => onChange(e.currentTarget.value)}
        required
        autoComplete="new-password"
        className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 pr-14 text-sm focus:border-[var(--accent)] focus:outline-none"
        style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider"
        style={{ color: 'var(--text-muted)' }}
      >
        {visible ? 'Ocultar' : 'Mostrar'}
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

function PreferencesCard() {
  // Single static theme for now — the app uses CSS variables, so the dark
  // theme is permanent. We display the choice for transparency.
  return (
    <SectionCard title="Preferencias" icon={Info}>
      <Row label="Tema" value={<span style={{ color: 'var(--text-primary)' }}>Oscuro (predeterminado)</span>} />
      <Row label="Idioma" value={<span style={{ color: 'var(--text-primary)' }}>Español (Perú)</span>} />
      <Row label="Norma activa" value={<span style={{ color: 'var(--text-primary)' }}>E.060 + ACI 318-19</span>} />
      <div className="mt-3 text-[10.5px] italic" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
        En próximas versiones podrás cambiar tema, idioma y unidades (SI / inglés).
      </div>
    </SectionCard>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10.5px] uppercase tracking-wider"
        style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}
